import Bowser from 'bowser';

export function throttle<P>(fn: (p: P) => void, delay: number) {
  let lastCall = 0;

  return (p: P) => {
    const now = Date.now();
    if (lastCall + delay > now) return;
    lastCall = now;
    fn(p);
  };
}

export function debounce<P>(fn: (p: P) => void, delay: number) {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const clear = () => {
    if (timeout !== null) clearTimeout(timeout);
  };

  return {
    run: (p: P) => {
      clear();
      timeout = setTimeout(() => {
        fn(p);
      }, delay);
    },
    clear,
  };
}

export function animationFrame<P = void>(fn: (p: P) => void) {
  let animationFrame: ReturnType<typeof requestAnimationFrame> | null = null;

  const clear = () => {
    if (animationFrame !== null) cancelAnimationFrame(animationFrame);
  };

  return {
    run: (p: P) => {
      clear();
      animationFrame = requestAnimationFrame(() => {
        fn(p);
      });
    },
    clear,
  };
}

export const createEventBus = <T>() => {
  const target = new EventTarget();

  return {
    dispatch(data?: T) {
      target.dispatchEvent(
        new CustomEvent<T>('message', {
          detail: data,
        }),
      );
    },

    subscribe(handler: (data?: T) => void) {
      const listener: EventListener = (event) => {
        handler((event as CustomEvent<T>).detail);
      };

      target.addEventListener('message', listener);

      return () => {
        target.removeEventListener('message', listener);
      };
    },
  };
};

export const decodeBase64 = (encoded?: string) => {
  if (!encoded) return '';
  try {
    if (typeof globalThis.atob === 'function') {
      const binary = globalThis.atob(encoded);
      if (typeof TextDecoder !== 'undefined') {
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        return new TextDecoder('utf-8').decode(bytes);
      }
      return binary;
    }
  } catch {
    return '';
  }
  return '';
};

export const flattenObject = (obj: Record<string, unknown>, prefix = '') => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === 'object') {
      Object.entries(flattenObject(value as never, newKey)).forEach(
        ([innerKey, innerValue]) => {
          acc[innerKey as never] = innerValue as never;
        },
      );
    } else {
      acc[newKey as never] = value as never;
    }

    return acc;
  }, {});
};

export const isStandalone = () =>
  window?.matchMedia('(display-mode: standalone)').matches ||
  (window?.navigator as any)?.standalone === true ||
  false;

export const getIsTelegramMiniApp = () => {
  if ((window as any).TelegramWebviewProxy) return true;
  const params = new URLSearchParams(window.location.search);
  return (
    params.has('tgWebAppData') ||
    params.has('tgWebAppStartParam') ||
    params.has('tgWebAppVersion') ||
    false
  );
};

export const hasRelatedApps = async () => {
  if (
    !('getInstalledRelatedApps' in window.navigator) ||
    typeof window.navigator.getInstalledRelatedApps !== 'function'
  )
    return false;
  try {
    const apps = await window.navigator.getInstalledRelatedApps();
    return Array.isArray(apps) && apps.length > 0;
  } catch (_e) {
    return false;
  }
};

export const withTimeout = <T>(
  promise: Promise<T>,
  ms = 2000,
): Promise<T | undefined> =>
  Promise.race([
    promise,
    new Promise<undefined>((resolve) =>
      setTimeout(() => resolve(undefined), ms),
    ),
  ]);

export const parseUserAgent = (ua: string) => {
  try {
    return Bowser.parse(ua);
  } catch (_e) {
    return null;
  }
};

export const clearCache = async (
  entries: (
    | 'cache' // Cache Storage (SW cache)
    | 'serviceWorker' // SW registrations
    | 'indexedDB' // IndexedDB
    | 'localStorage'
    | 'sessionStorage'
    | 'cookies'
    | 'credentials'
  )[],
) => {
  const actions: Promise<unknown>[] = [];
  if (entries.includes('cache') && 'caches' in window) {
    actions.push(
      withTimeout(
        caches
          .keys()
          .then((names) => Promise.all(names.map((n) => caches.delete(n)))),
      ),
    );
  }
  if (entries.includes('serviceWorker') && 'serviceWorker' in navigator) {
    actions.push(
      withTimeout(
        navigator.serviceWorker
          .getRegistrations()
          .then((regs) => Promise.all(regs.map((r) => r.unregister()))),
      ),
    );
  }
  if (entries.includes('indexedDB') && indexedDB?.databases) {
    actions.push(
      withTimeout(
        indexedDB.databases().then((dbs) =>
          Promise.all(
            dbs
              .filter((db) => db.name)
              .map(
                (db) =>
                  new Promise((resolve) => {
                    const req = indexedDB.deleteDatabase(db.name!);
                    req.onsuccess =
                      req.onerror =
                      req.onblocked =
                        () => resolve(undefined);
                  }),
              ),
          ),
        ),
      ),
    );
  }
  if (entries.includes('localStorage')) {
    try {
      localStorage.clear();
    } catch {}
  }
  if (entries.includes('sessionStorage')) {
    try {
      sessionStorage.clear();
    } catch {}
  }
  if (entries.includes('cookies')) {
    try {
      document.cookie
        .split(';')
        .map((c) => c.trim())
        .forEach((cookie) => {
          const eq = cookie.indexOf('=');
          const name = eq > -1 ? cookie.slice(0, eq) : cookie;
          // biome-ignore lint/suspicious/noDocumentCookie: its for clearing cache
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        });
    } catch {}
  }
  if (entries.includes('credentials') && navigator.credentials) {
    actions.push(withTimeout(navigator.credentials.preventSilentAccess()));
  }
  await Promise.allSettled(actions);
  const newUrl = new URL(location.href);
  newUrl.searchParams.set('__reload', Date.now().toString());
  location.href = newUrl.href;
};
